import { Navigate, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Rocket } from "lucide-react";
import { Button, Card, CardContent } from "@eduviaos/ui-kit";
import { useAuth } from "@/auth/AuthProvider";
import { FirstRunShell } from "@/components/FirstRunShell";
import { buildFirstRunSteps, firstRunEyebrow } from "@/components/firstRunSteps";
import { useCompleteFirstRun } from "@/hooks/useFirstRun";
import { useEnabledModules } from "@/hooks/useEnabledModules";

const GO_LIVE_CHECKLIST = [
  "Branding applied to your admin portal and learner portal",
  "Team invites sent (or skipped — you can invite anytime from People)",
  "Learner roster imported or ready to add from Admissions",
  "Default notification triggers reviewed",
] as const;

/**
 * Phase SC — first-run step 6 (study centre only): go-live confirmation.
 *
 * Flips ``tenant.first_run_complete`` so staff dashboards unlock and the
 * principal lands on the operational dashboard.
 */
export function FirstRunGoLivePage() {
  const navigate = useNavigate();
  const { firstRunComplete, status } = useAuth();
  const { vertical } = useEnabledModules();
  const completeMutation = useCompleteFirstRun();
  const resolvedVertical = vertical ?? "school";

  if (resolvedVertical !== "study_centre") {
    return <Navigate to="/dashboard" replace />;
  }

  if (status === "authed" && firstRunComplete === true) {
    return <Navigate to="/dashboard" replace />;
  }

  const steps = buildFirstRunSteps("study_centre");

  const onGoLive = () => {
    completeMutation.mutate(undefined, {
      onSuccess: () => {
        setTimeout(() => navigate("/dashboard", { replace: true }), 200);
      },
    });
  };

  return (
    <FirstRunShell
      steps={steps}
      activeStep="go_live"
      eyebrow={firstRunEyebrow("study_centre", "go_live")}
      showSkipExit={false}
    >
      <header className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 grid gap-3 text-center motion-safe:duration-500">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          You&apos;re ready to go live
        </h1>
        <p className="text-muted-foreground mx-auto max-w-xl text-sm leading-relaxed sm:text-base">
          Your study centre workspace is configured. Going live unlocks the full dashboard for your
          team and turns on learner-facing flows.
        </p>
      </header>

      <Card className="mx-auto mt-8 max-w-lg">
        <CardContent className="grid gap-4 p-6">
          {GO_LIVE_CHECKLIST.map((line) => (
            <div key={line} className="flex items-start gap-3 text-sm">
              <CheckCircle2 aria-hidden="true" className="text-primary mt-0.5 size-4 shrink-0" />
              <span>{line}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="mt-10 flex justify-center">
        <Button size="lg" onClick={onGoLive} disabled={completeMutation.isPending}>
          {completeMutation.isPending ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          ) : (
            <Rocket aria-hidden="true" className="size-4" />
          )}
          Go live
        </Button>
      </div>
    </FirstRunShell>
  );
}
